import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, AlertCircle, Loader2, Gem } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function AcceptQuoteOption() {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading, success, already_accepted, error
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const acceptQuoteOption = async () => {
      try {
        const response = await axios.get(`${API}/api/public/accept-quote-option/${token}`);
        if (response.data.already_accepted) {
          setStatus('already_accepted');
        } else {
          setStatus('success');
        }
        setData(response.data);
      } catch (err) {
        setStatus('error');
        const detail = err.response?.data?.detail;
        const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to accept quote option. Please contact us directly.');
        setError(msg);
      }
    };

    if (token) {
      acceptQuoteOption();
    }
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardContent className="p-8">
          {status === 'loading' && (
            <div className="text-center">
              <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-800">Processing your selection...</h2>
              <p className="text-gray-600 mt-2">Please wait a moment.</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-green-700 mb-2">Quote Option Accepted!</h2>
              <p className="text-gray-600 mb-4">
                Thank you, {data?.client_name}! Your selection for order <strong>#{data?.order_number}</strong> has been confirmed.
              </p>
              
              {/* Selected Stone Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Gem className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-blue-800">Your Selected Stone</span>
                </div>
                <p className="text-2xl font-bold text-blue-700">{data?.selected_stone}</p>
                {data?.total && (
                  <p className="text-lg text-blue-600 mt-1">Total: £{data.total.toFixed(2)}</p>
                )}
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-left">
                <h3 className="font-semibold text-green-800 mb-2">What happens next?</h3>
                <ul className="text-green-700 text-sm space-y-2">
                  <li>✓ We've been notified of your stone selection</li>
                  <li>✓ We'll contact you shortly to arrange the 55% deposit</li>
                  <li>✓ We'll schedule templating/fitting at your convenience</li>
                </ul>
              </div>
              <p className="text-gray-500 text-sm mt-6">
                If you have any questions, please call us or reply to the quote email.
              </p>
            </div>
          )}

          {status === 'already_accepted' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-blue-700 mb-2">Already Accepted</h2>
              <p className="text-gray-600 mb-4">
                This quote <strong>#{data?.order_number}</strong> has already been accepted.
              </p>
              {data?.selected_stone && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-blue-700">Selected stone: <strong>{data.selected_stone}</strong></p>
                </div>
              )}
              <p className="text-gray-500 text-sm">
                If you haven't heard from us, please get in touch and we'll help you.
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-12 h-12 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-red-700 mb-2">Something Went Wrong</h2>
              <p className="text-gray-600 mb-4">{error}</p>
              <p className="text-gray-500 text-sm">
                Please contact us directly at <a href="mailto:worktops@setinstonedidcot.co.uk" className="text-blue-600 underline">worktops@setinstonedidcot.co.uk</a>
              </p>
            </div>
          )}

          <div className="mt-8 pt-6 border-t text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_263de3b3-65cb-454c-833e-b2e4fd38f996/artifacts/8fq6fdw7_email%20logo.png" 
              alt="Set In Stone Didcot Ltd" 
              className="h-12 mx-auto mb-2"
            />
            <p className="text-gray-500 text-sm">Set In Stone Didcot Ltd</p>
            <p className="text-gray-400 text-xs">Quality Worktops & Hearths</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default AcceptQuoteOption;
